import { getCurrentServer, getOpenAccessTokenKey, getPrefixUrl } from '@/utils/globalData'
import { getRedirectSubDomain, normalizeTrackingHost } from '@/utils/eventTracking'
import { useMainStore } from '@/composables/store'

export const ONLINE_VISIT_ENDPOINT = '/mall/admin/online/recordVisit?isShop=1&uncheck401=1'

export const ONLINE_VISIT_PAGE_TYPES = Object.freeze({
  SHOP_HOME: 'SHOP_HOME',
  PRODUCT_DETAIL: 'PRODUCT_DETAIL',
  CHECKOUT: 'CHECKOUT',
  ADD_CART: 'ADD_CART',
  OTHER: 'OTHER',
})

const VISITOR_ID_STORAGE_KEY = 'mall_online_visitor_id'
const VISITOR_ID_COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60
const ADD_CART_REPORT_DEDUP_MS = 1000

let lastAddCartReportAt = 0

const normalizeValue = (value) => String(value ?? '').trim()

const safeParseJson = (value) => {
  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

const readCookieValue = (name) => {
  if (!process.client || typeof document === 'undefined') return ''

  try {
    const encodedName = encodeURIComponent(name)
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${encodedName}=([^;]*)`))
    return match?.[1] ? decodeURIComponent(match[1]) : ''
  } catch (error) {
    return ''
  }
}

const writeCookieValue = (name, value) => {
  if (!process.client || typeof document === 'undefined') return

  try {
    const parts = [
      `${encodeURIComponent(name)}=${encodeURIComponent(value)}`,
      'Path=/',
      `Max-Age=${VISITOR_ID_COOKIE_MAX_AGE_SECONDS}`,
      'SameSite=Lax',
    ]

    if (window.location.protocol === 'https:') {
      parts.push('Secure')
    }

    document.cookie = parts.join('; ')
  } catch (error) {
    // Ignore cookie persistence errors; reporting must never block the page.
  }
}

const readLocalStorageValue = (key) => {
  if (!process.client || typeof localStorage === 'undefined') return ''

  try {
    return normalizeValue(localStorage.getItem(key))
  } catch (error) {
    return ''
  }
}

const writeLocalStorageValue = (key, value) => {
  if (!process.client || typeof localStorage === 'undefined' || !value) return

  try {
    localStorage.setItem(key, value)
  } catch (error) {
    // Ignore storage errors; cookie fallback still gives us a visitor id.
  }
}

const getMainStoreSafely = () => {
  try {
    return useMainStore()
  } catch (error) {
    return null
  }
}

const getCurrentUrl = () => {
  if (!process.client || typeof window === 'undefined') return ''
  return `${window.location.pathname || '/'}${window.location.search || ''}`
}

export const getOnlineVisitorId = () => {
  if (!process.client) return ''

  const storedVisitorId =
    readLocalStorageValue(VISITOR_ID_STORAGE_KEY) ||
    normalizeValue(readCookieValue(VISITOR_ID_STORAGE_KEY))

  if (storedVisitorId) {
    return storedVisitorId
  }

  const randomPart = Math.random().toString(36).slice(2, 10)
  const visitorId = `web_${randomPart}_${Date.now()}`
  writeLocalStorageValue(VISITOR_ID_STORAGE_KEY, visitorId)
  writeCookieValue(VISITOR_ID_STORAGE_KEY, visitorId)

  return visitorId
}

export const getOnlineVisitDomain = () => {
  if (!process.client || typeof window === 'undefined') return ''

  return normalizeTrackingHost(
    getRedirectSubDomain() ||
    window.location.host ||
    ''
  )
}

export const resolveOnlineVisitShopId = () => {
  if (!process.client) return ''

  const store = getMainStoreSafely()
  const currentServer = getCurrentServer()
  const tokenKey = getOpenAccessTokenKey(currentServer)
  const candidates = [
    typeof store?.getShopId === 'function' ? store.getShopId(currentServer) : '',
    typeof store?.getDealerShopId === 'function' ? store.getDealerShopId(currentServer) : '',
    typeof store?.getServerShopId === 'function' ? store.getServerShopId(currentServer) : '',
    store?.$state?.shopIdKeyMap?.[tokenKey],
    store?.$state?.dealerShopIdKeyMap?.[tokenKey],
    readCookieValue('dealerShopId'),
    readLocalStorageValue('shopId'),
    readLocalStorageValue('dealerShopId'),
  ]

  return normalizeValue(candidates.find((item) => normalizeValue(item)))
}

const resolveOnlineVisitUserId = () => {
  if (!process.client) return ''

  const store = getMainStoreSafely()
  const localUserInfo =
    safeParseJson(readLocalStorageValue('userInfo')) ||
    safeParseJson(readLocalStorageValue('mallUserInfo')) ||
    {}

  const candidates = [
    store?.mallUserInfo?.id,
    store?.mallUserInfo?.userId,
    store?.mallUserInfo?.customerInfoId,
    store?.userInfo?.id,
    store?.userInfo?.userId,
    store?.userInfo?.customerInfoId,
    localUserInfo?.id,
    localUserInfo?.userId,
    localUserInfo?.customerInfoId,
  ]

  return normalizeValue(candidates.find((item) => normalizeValue(item)))
}

export const resolveOnlineVisitPageType = (url = '') => {
  const rawUrl = normalizeValue(url) || getCurrentUrl()
  const path = (rawUrl.split('?')[0] || '/').toLowerCase()

  if (path === '/' || path === '/index') {
    return ONLINE_VISIT_PAGE_TYPES.SHOP_HOME
  }

  if (path.startsWith('/payment') || path.startsWith('/checkout')) {
    return ONLINE_VISIT_PAGE_TYPES.CHECKOUT
  }

  if (
    path === '/product' ||
    path.startsWith('/product/detail') ||
    path.startsWith('/products/')
  ) {
    return ONLINE_VISIT_PAGE_TYPES.PRODUCT_DETAIL
  }

  return ONLINE_VISIT_PAGE_TYPES.OTHER
}

export const buildOnlineVisitPayload = (options = {}) => {
  const url = normalizeValue(options.url) || getCurrentUrl()
  const pageType = normalizeValue(options.pageType) || resolveOnlineVisitPageType(url)
  const userId = resolveOnlineVisitUserId()
  const param = {
    domain: getOnlineVisitDomain(),
    visitorId: getOnlineVisitorId(),
    pageType,
    url,
  }

  if (userId) {
    param.userId = userId
  }

  return { param }
}

const buildOnlineVisitHeaders = () => {
  const shopId = resolveOnlineVisitShopId()

  if (!shopId) {
    return {}
  }

  return {
    Shopid: shopId,
    shopId,
    shopid: shopId,
  }
}

export const sendOnlineVisit = async (pageType, options = {}) => {
  if (!process.client) return null

  try {
    const nuxtApp = options.nuxtApp || useNuxtApp()
    const payload = buildOnlineVisitPayload({
      ...options,
      pageType,
    })

    if (!payload.param.domain || !payload.param.visitorId) {
      return null
    }

    return await nuxtApp.$customFetch(ONLINE_VISIT_ENDPOINT, {
      method: 'POST',
      headers: buildOnlineVisitHeaders(),
      body: payload,
    })
  } catch (error) {
    return null
  }
}

export const reportAddCartVisit = (options = {}) => {
  if (!process.client) return null

  const now = Date.now()
  if (now - lastAddCartReportAt < ADD_CART_REPORT_DEDUP_MS) {
    return null
  }

  lastAddCartReportAt = now
  return sendOnlineVisit(ONLINE_VISIT_PAGE_TYPES.ADD_CART, options)
}

export const sendOnlineVisitKeepalive = (pageType, options = {}) => {
  if (!process.client || typeof fetch !== 'function') return

  try {
    const payload = buildOnlineVisitPayload({
      ...options,
      pageType,
    })

    if (!payload.param.domain || !payload.param.visitorId) {
      return
    }

    fetch(`${getPrefixUrl()}${ONLINE_VISIT_ENDPOINT}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...buildOnlineVisitHeaders(),
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {})
  } catch (error) {
    // no-op
  }
}
